/**
 * "I replied" — the operator telling Tacit what Tacit could not see.
 *
 * The Today queue reads `action_state`, and `action_state` only ever moved when
 * a message arrived through HeyReach. A reply typed by hand in LinkedIn left no
 * trace, so the queue kept asking for work already done. This dialog is the
 * missing input: one statement, one activity row, one turn handed back.
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Textarea } from '@/components/ui/textarea';
import { useLogReply } from '@/hooks/crm/useContacts';
import {
  REPLY_CHANNELS,
  REPLY_CHANNEL_LABELS,
  defaultFollowUpDate,
  type ReplyChannel,
} from '@/lib/crm/log-reply';
import type { Contact } from '@/lib/crm/types';

interface LogReplyDialogProps {
  contact: Contact;
  onClose: () => void;
}

interface LogReplyFormValues {
  channel: ReplyChannel;
  sentOn: string;
  body: string;
  followUpOn: string;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const logReplySchema = z.object({
  channel: z.enum(REPLY_CHANNELS),
  sentOn: z.string().regex(ISO_DATE, 'Pick the day the reply went out.'),
  body: z.string().max(20000),
  followUpOn: z.string().regex(ISO_DATE, 'Pick a day to chase on.'),
});

function todayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

export function LogReplyDialog({ contact, onClose }: LogReplyDialogProps): JSX.Element {
  const logReply = useLogReply();
  const initialSentOn = todayIso();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<LogReplyFormValues>({
    resolver: zodResolver(logReplySchema),
    defaultValues: {
      channel: 'linkedin',
      sentOn: initialSentOn,
      body: '',
      followUpOn: defaultFollowUpDate(initialSentOn),
    },
  });

  const sentOn = watch('sentOn');

  const onSubmit = async (values: LogReplyFormValues): Promise<void> => {
    try {
      await logReply.mutateAsync({ contact, input: values });
      // "waiting on you" reads backwards — the whole point is that the turn
      // just moved to them. Say whose move it is, and when it comes back.
      toast.success(`Recorded. The ball is with ${contact.name} — chase on ${values.followUpOn}.`);
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'The reply could not be recorded. Try again.',
      );
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Record a reply you sent</DialogTitle>
          <DialogDescription>
            Tacit only sees messages that arrive through the integration. Tell it what you sent
            yourself, and this contact stops asking for a response it already got.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <p className="break-words font-medium" data-content-origin="external">
              {contact.name}
            </p>
            <p className="break-words text-sm text-muted-foreground" data-content-origin="external">
              {[contact.role, contact.company].filter(Boolean).join(' · ') || '—'}
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="log-reply-channel">Channel</Label>
              <NativeSelect id="log-reply-channel" {...register('channel')}>
                {REPLY_CHANNELS.map((channel) => (
                  <option key={channel} value={channel}>
                    {REPLY_CHANNEL_LABELS[channel]}
                  </option>
                ))}
              </NativeSelect>
            </div>

            <div className="space-y-1">
              <Label htmlFor="log-reply-sent-on">Sent on</Label>
              <Input
                id="log-reply-sent-on"
                type="date"
                {...register('sentOn', {
                  // Re-anchor the chase date whenever the send date moves, so
                  // logging an old reply produces an already-overdue chase
                  // rather than quietly granting another week.
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                    setValue('followUpOn', defaultFollowUpDate(event.target.value)),
                })}
              />
              {errors.sentOn && (
                <p className="text-sm text-destructive" role="alert">{errors.sentOn.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="log-reply-body">What you said (optional)</Label>
            <Textarea
              id="log-reply-body"
              rows={6}
              placeholder="Paste the message. It is kept with the contact so the next person reads the conversation, not a summary of it."
              {...register('body')}
              data-content-origin="user"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="log-reply-follow-up">Chase on</Label>
            <Input id="log-reply-follow-up" type="date" {...register('followUpOn')} />
            <p className="text-xs text-muted-foreground">
              Defaults to a week after {sentOn || 'the send date'}, skipping the weekend.
            </p>
            {errors.followUpOn && (
              <p className="text-sm text-destructive" role="alert">{errors.followUpOn.message}</p>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" className="min-h-11 md:min-h-11" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="min-h-11 md:min-h-11" disabled={logReply.isPending}>
              {logReply.isPending ? 'Recording…' : 'Record the reply'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
